import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '../lib/functions';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Returns true when the error is a Vite chunk-load failure after a new deploy. */
function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module')
  );
}

const RELOAD_FLAG = 'cc_chunk_reload';

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // If this is a stale-chunk error and we haven't already auto-reloaded, do so.
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();
      // Return hasError: false — the reload will happen before React paints anything.
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
    // Tell ParkerTech — fire-and-forget so reporting can never make the crash worse.
    if (!isChunkLoadError(error)) {
      reportClientError({
        message: error.message,
        stack: error.stack ?? '',
        componentStack: info.componentStack ?? '',
        path: window.location.pathname,
        userAgent: navigator.userAgent,
      }).catch(() => { /* reporting is best-effort */ });
    }
    // Clear the reload flag on any error that isn't a chunk error, so future
    // navigations can still trigger a reload if needed.
    if (!isChunkLoadError(error)) {
      sessionStorage.removeItem(RELOAD_FLAG);
    }
  }

  handleReset = () => {
    sessionStorage.removeItem(RELOAD_FLAG);
    this.setState({ hasError: false, error: null });
    // Navigate home so we don't immediately re-throw the same error.
    window.location.href = '/';
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-7 w-7 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm text-gray-500">
              An unexpected error occurred. Please try returning to the home screen.
            </p>
            {this.state.error && (
              <details className="mb-6 rounded-lg bg-gray-50 p-3 text-left">
                <summary className="cursor-pointer text-xs font-medium text-gray-500">
                  Error details
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-all text-xs text-red-600">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <button
              onClick={this.handleReset}
              className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
            >
              Return to home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
